<template>
    <div class="ms-page">
        <!-- ─────────────────── HEADER ─────────────────── -->
        <header class="ms-header">
            <div class="ms-header__inner">
                <div class="ms-header__title-wrap">
                    <h1 class="ms-title">מנויי הלקוח</h1>
                    <span class="ms-total-pill" :title="`סה״כ ${manuim.length} מנויים`">
                        <v-icon size="15">mdi-sim</v-icon>
                        <span>{{ manuim.length.toLocaleString('he-IL') }}</span>
                    </span>
                </div>

                <div class="ms-header__side">
                    <!-- התראת חוב - מוצגת רק אם הבדיקה השקטה החזירה חיובים שלא נגבו -->
                    <transition name="ms-debt-fade" mode="out-in">
                        <span v-if="checkingFailedBills" key="checking" class="ms-debt-probe" aria-live="polite">
                            <span class="ms-debt-probe__ring"></span>
                            <span class="ms-debt-probe__text">בודק חיובים פתוחים…</span>
                        </span>

                        <button v-else-if="hasFailedBills" key="debt" type="button" class="ms-debt-chip"
                            @click="failedBillsDialog = true"
                            :aria-label="`ללקוח ${failedBillsCount} חיובים שלא נגבו - לצפייה בפירוט`">
                            <span class="ms-debt-chip__icon">
                                <v-icon size="16">mdi-alert-circle-outline</v-icon>
                            </span>
                            <span class="ms-debt-chip__body">
                                <span class="ms-debt-chip__label">חיובים שלא נגבו</span>
                                <span class="ms-debt-chip__value">
                                    {{ formatCurrency(failedBillsTotal) }}
                                    <span class="ms-debt-chip__count">{{ failedBillsCount }} חיובים</span>
                                </span>
                            </span>
                            <v-icon size="16" class="ms-debt-chip__chev">mdi-chevron-left</v-icon>
                        </button>
                    </transition>

                    <div class="ms-customer-card">
                        <div class="ms-customer-card__icon">
                            <v-icon size="16">mdi-account-outline</v-icon>
                        </div>
                        <div class="ms-customer-card__body">
                            <span class="ms-customer-card__label">לקוח</span>
                            <span class="ms-customer-card__name">{{ customerName || '—' }}</span>
                        </div>
                        <span v-if="customerCardId" class="ms-customer-card__id">{{ customerCardId }}</span>
                    </div>
                </div>
            </div>
        </header>

        <ProgressShow v-if="progressShow" v-model="progressShow"></ProgressShow>

        <!-- ─────────────────── MAIN ─────────────────── -->
        <main class="ms-main">
            <!-- STATUS SUMMARY -->
            <section class="ms-summary-grid" aria-label="סיכום סטטוסים">
                <div class="ms-summary-card ms-summary-card--all">
                    <div class="ms-summary-card__icon"><v-icon size="19">mdi-sim</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">סה״כ מנויים</span>
                        <strong class="ms-summary-card__value">{{ manuim.length.toLocaleString('he-IL') }}</strong>
                    </div>
                </div>

                <div class="ms-summary-card ms-summary-card--active">
                    <div class="ms-summary-card__icon"><v-icon size="19">mdi-check-circle-outline</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">פעילים</span>
                        <strong class="ms-summary-card__value">{{manuim.filter(item => item.status ===
                            'active').length.toLocaleString('he-IL')}}</strong>
                    </div>
                </div>

                <div class="ms-summary-card ms-summary-card--process">
                    <div class="ms-summary-card__icon"><v-icon size="19">mdi-clock-outline</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">בתהליך</span>
                        <strong class="ms-summary-card__value">{{manuim.filter(item => item.status ===
                            'proccess').length.toLocaleString('he-IL')}}</strong>
                    </div>
                </div>

                <div class="ms-summary-card ms-summary-card--inactive">
                    <div class="ms-summary-card__icon"><v-icon size="19">mdi-cancel</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">לא פעילים</span>
                        <strong class="ms-summary-card__value">{{manuim.filter(item => item.status ===
                            'not_active').length.toLocaleString('he-IL')}}</strong>
                    </div>
                </div>
            </section>

            <!-- TOOLBAR -->
            <div class="ms-toolbar">
                <div class="ms-search">
                    <v-icon class="ms-search__icon" size="19">mdi-magnify</v-icon>
                    <input v-model="search" type="text" class="ms-search__input"
                        placeholder="חיפוש לפי מספר מנוי, סים, מפעיל או חבילה…" />
                    <button v-if="search" type="button" class="ms-search__clear" @click="search = ''"
                        aria-label="נקה חיפוש">
                        <v-icon size="14">mdi-close</v-icon>
                    </button>
                </div>

                <div class="ms-toolbar__hint">
                    <v-icon size="16">mdi-information-outline</v-icon>
                    <span>ניתן לבצע פעולות מהירות ישירות מתוך הטבלה</span>
                </div>

                <div class="ms-pagesize">
                    <label for="ms-pagesize-select">שורות</label>
                    <select id="ms-pagesize-select" class="ms-pagesize__select" v-model.number="itemsPerPage">
                        <option v-for="n in [20, 50, 100, 500, 1000]" :key="n" :value="n">{{ n }}</option>
                    </select>
                </div>
            </div>

            <!-- TABLE -->
            <div class="ms-table-card">
                <v-data-table id="table1" :headers="headers" :items="manuim" hide-default-footer :search="search"
                    :page.sync="currentPage" :items-per-page="itemsPerPage" @page-count="pageCount = $event"
                    class="ms-table">

                    <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
                        <span class="ms-th">{{ header.text }}</span>
                    </template>

                    <template v-slot:item.index="{ index }">
                        <span class="ms-index">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>
                    </template>

                    <template v-slot:item.status="{ item }">
                        <!-- 1. נמחק בפועל -->
                        <v-tooltip bottom v-if="item.status === 'not_active'" transition="scale-transition"
                            open-delay="150" close-delay="100" color="#2E2E2E" max-width="260"
                            content-class="custom-tooltip">
                            <template v-slot:activator="{ on }">
                                <span v-on="on" class="ms-status-pill ms-status-pill--inactive cursor-pointer">
                                    <span class="ms-status-pill__dot"></span>
                                    לא פעיל
                                </span>
                            </template>
                            <div class="tooltip-content">
                                <div class="tooltip-title">המנוי בוטל</div>
                                <div class="tooltip-line">
                                    <v-icon x-small color="#fdb721" class="ml-1">mdi-calendar</v-icon>
                                    נמחק בתאריך {{ formatDateString(item.deletedAt) }}
                                </div>
                                <div class="tooltip-line">
                                    <v-icon x-small color="#8066f0" class="ml-1">mdi-account</v-icon>
                                    על ידי {{ item.deletedBy && item.deletedBy.name || 'לא ידוע' }}
                                </div>
                            </div>
                        </v-tooltip>

                        <!-- 2. פעיל, אך קיים תאריך מחיקה מתוכנן -->
                        <v-menu v-else-if="isPendingCancellation(item)" open-on-hover offset-y bottom open-delay="150"
                            close-delay="220" nudge-bottom="4" min-width="250" transition="scale-transition"
                            content-class="ms-pending-menu">
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="{ ...on, click: on.mouseenter }"
                                    class="ms-status-pill ms-status-pill--active ms-status-pill--pending cursor-pointer">
                                    <span class="ms-status-pill__dot"></span>
                                    פעיל
                                    <span
                                        :class="['ms-pending-badge', isDeletionOverdue(item) && 'ms-pending-badge--overdue']">
                                        <v-icon class="ms-pending-badge__icon">
                                            {{ isDeletionOverdue(item) ? 'mdi-alert-outline' : 'mdi-clock-alert-outline'
                                            }}
                                        </v-icon>
                                        {{ pendingLabel(item) }}
                                    </span>
                                </span>
                            </template>

                            <div class="tooltip-content">
                                <div v-if="isDeletionOverdue(item)" class="tooltip-title tooltip-title--danger">
                                    <v-icon x-small color="#ff6b6b" class="ml-1">mdi-alert-circle-outline</v-icon>
                                    תאריך המחיקה עבר
                                </div>
                                <div v-else class="tooltip-title tooltip-title--warn">
                                    <v-icon x-small color="#ffb01f" class="ml-1">mdi-progress-clock</v-icon>
                                    מתוכנן למחיקה
                                </div>
                                <div class="tooltip-line">
                                    <v-icon x-small color="#fdb721" class="ml-1">mdi-calendar-clock</v-icon>
                                    תאריך מחיקה: {{ formatDateOnly(deletionDate(item)) }}
                                </div>
                                <div class="tooltip-line">
                                    <v-icon x-small color="#8066f0" class="ml-1">mdi-account</v-icon>
                                    בוטל על ידי {{ item.deletedBy && item.deletedBy.name || 'לא ידוע' }}
                                </div>
                                <div class="tooltip-note">
                                    {{ isDeletionOverdue(item)
                                        ? 'המנוי עדיין פעיל למרות שתאריך המחיקה חלף — נדרשת בדיקה'
                                        : 'המנוי יישאר פעיל עד למועד זה' }}
                                </div>

                                <button v-if="hasPermission('delete_manui')" type="button" class="ms-tt-btn"
                                    @click="openCancelDeleteDialog(item)">
                                    <v-icon size="15" class="ml-1" color="#8ce99a">mdi-delete-off-outline</v-icon>
                                    ביטול תהליך המחיקה
                                </button>
                            </div>
                        </v-menu>

                        <!-- 3. פעיל רגיל / בתהליך -->
                        <span v-else
                            :class="['ms-status-pill', item.status === 'active' ? 'ms-status-pill--active' : 'ms-status-pill--process']">
                            <span class="ms-status-pill__dot"></span>
                            {{ item.status === 'active' ? 'פעיל' : 'בתהליך' }}
                        </span>
                    </template>

                    <template v-slot:item.manui_number="{ item }">
                        <div class="ms-inline-cell">
                            <span class="ms-number">{{ item.manui_number || '—' }}</span>
                            <v-tooltip bottom
                                v-if="item.status === 'proccess' && item.manui_number && hasPermission('update_manui_number')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn"
                                        @click="openEditPhoneDialog(item)" aria-label="עדכון מספר מנוי">
                                        <v-icon size="15">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>עדכון מספר מנוי</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.plan_id.operating_company="{ item }">
                        <div class="ms-inline-cell">
                            <span class="ms-company-badge">{{ companiesList[item.plan_id.operating_company] }}</span>
                            <v-tooltip bottom
                                v-if="item.status !== 'proccess' && item.manui_number && hasPermission('update_company')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn"
                                        @click="openEditcompanyDialog(item)" aria-label="החלפת מפעיל">
                                        <v-icon size="15">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>החלפת מפעיל</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.sim_number="{ item }">
                        <div class="ms-inline-cell">
                            <span class="ms-number">{{ item.sim_number || '—' }}</span>
                            <v-tooltip bottom
                                v-if="hasPermission('update_sim_number') && (item.status === 'proccess' || !item.sim_number)">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn" @click="openEditSimDialog(item)"
                                        aria-label="עדכון מספר סים">
                                        <v-icon size="15">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>{{ item.status === 'proccess' ? 'עדכון מספר סים' : 'השלמת מספר סים' }}</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.plan_id.name="{ item }">
                        <div class="ms-inline-cell">
                            <span class="ms-plan-name">{{ item.plan_id.name }}</span>
                            <v-tooltip bottom v-if="item.status === 'active'">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn"
                                        @click="openEditPlanDialog(item)" aria-label="עדכון חבילה">
                                        <v-icon size="15">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>עדכון חבילה</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.billing_day="{ item }">
                        <span v-if="checkingFailedBills" class="ms-billing-day ms-billing-day--muted">…</span>
                        <v-tooltip bottom v-else-if="billingInfo(item)" transition="scale-transition" open-delay="150"
                            close-delay="100" color="#2E2E2E" max-width="260" content-class="custom-tooltip">
                            <template v-slot:activator="{ on }">
                                <span v-on="on" class="ms-billing-day">
                                    <v-icon size="14" class="ms-billing-day__icon">mdi-calendar-outline</v-icon>
                                    {{ billingInfo(item).day }} לחודש
                                </span>
                            </template>
                            <div class="tooltip-content">
                                <div v-if="item.status === 'active' && billingInfo(item).nextBillDate"
                                    class="tooltip-line">
                                    <v-icon x-small color="#fdb721" class="ml-1">mdi-calendar-clock</v-icon>
                                    חיוב הבא: {{ billingInfo(item).nextBillDate }}
                                </div>
                                <div v-if="billingInfo(item).lastBillDate" class="tooltip-line">
                                    <v-icon x-small color="#8066f0" class="ml-1">mdi-history</v-icon>
                                    חיוב אחרון: {{ billingInfo(item).lastBillDate }}
                                    {{ billingInfo(item).lastStatus === 'DEBTAUTOBILLING' ? '(לא נגבה)' : '' }}
                                </div>
                            </div>
                        </v-tooltip>
                        <span v-else class="ms-billing-day ms-billing-day--muted">—</span>
                    </template>

                    <template v-slot:item.actions="{ item, index }">
                        <div class="ms-actions">
                            <v-tooltip bottom
                                v-if="item.status === 'active' && item.manui_number && hasPermission('edit_manui')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--callblock"
                                        @click="openCallBlockDialog(item)" aria-label="חסימת חיוג">
                                        <v-icon size="17">mdi-phone-lock</v-icon>
                                    </button>
                                </template>
                                <span>חסימת חיוג</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="hasOperatorActions(item) && hasPermission('operator_actions')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--operator"
                                        @click="openOperatorActionsDialog(item)" aria-label="פעולות מול ספק">
                                        <v-icon size="17">mdi-cellphone-cog</v-icon>
                                    </button>
                                </template>
                                <span>פעולות מול ספק סלולר</span>
                            </v-tooltip>
                            <v-tooltip bottom v-if="hasPermission('edit_manui') && item.status !== 'not_active'">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--edit"
                                        @click="openEditDialog(item, index)" aria-label="עריכת מנוי">
                                        <v-icon size="17">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>עריכה</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="role === 'admin'">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--customer"
                                        @click="openEditCustomerDialog(item, index)" aria-label="שינוי לקוח">
                                        <v-icon size="17">mdi-account-switch-outline</v-icon>
                                    </button>
                                </template>
                                <span>שינוי לקוח</span>
                            </v-tooltip>

                            <!-- <v-tooltip bottom v-if="role !== 'agent'">
                                <template v-slot:activator="{ on }">
                                    <v-badge :content="item.ticketsCount" :value="item.ticketsCount > 0" color="#0d2c6d"
                                        overlap offset-x="10" offset-y="8">
                                        <button v-on="on" type="button" class="ms-action-btn ms-action-btn--ticket"
                                            @click="createTicket(item, index)" aria-label="פתיחת פנייה">
                                            <v-icon size="17">mdi-email-fast-outline</v-icon>
                                        </button>
                                    </v-badge>
                                </template>
                                <span>פתח פנייה למנוי זה</span>
                            </v-tooltip> -->

                            <v-tooltip bottom v-if="item.status !== 'active' && hasPermission('delete_manui')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--delete"
                                        @click="openDeleteDialog(item, index)" aria-label="מחיקת מנוי">
                                        <v-icon size="17">mdi-delete-outline</v-icon>
                                    </button>
                                </template>
                                <span>מחיקה</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:no-data>
                        <div class="ms-empty">
                            <div class="ms-empty__icon"><v-icon size="30">mdi-sim-off</v-icon></div>
                            <h3 class="ms-empty__title">לא נמצאו מנויים</h3>
                            <p class="ms-empty__hint">כאשר יהיו ללקוח מנויים, הם יוצגו כאן.</p>
                        </div>
                    </template>

                    <template v-slot:no-results>
                        <div class="ms-empty">
                            <div class="ms-empty__icon"><v-icon size="30">mdi-magnify-close</v-icon></div>
                            <h3 class="ms-empty__title">לא נמצאו תוצאות</h3>
                            <p class="ms-empty__hint">נסה לחפש מספר מנוי, סים, מפעיל או חבילה אחרת.</p>
                        </div>
                    </template>
                </v-data-table>

                <div v-if="pageCount > 1" class="ms-pagination-wrap">
                    <v-pagination v-model="currentPage" circle :length="pageCount" :total-visible="7"></v-pagination>
                </div>
            </div>
        </main>

        <v-overlay :value="overlay">
            <v-progress-circular indeterminate size="84" color="#0d2c6d"></v-progress-circular>
        </v-overlay>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>

        <EditDialog v-model="addEditDialog" v-if="addEditDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            :customerId="customerId" @itemEdited="editedItemSaved" />

        <EditPlanDialog v-model="editPlanDialog" v-if="editPlanDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <EditSimDialog v-model="editSimDialog" v-if="editSimDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <EditCustomerDialog v-model="EditCustomerDialog" v-if="EditCustomerDialog" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <EditPhoneDialog v-model="editPhoneDialog" v-if="editPhoneDialog" :formTitle="formTitle"
            :itemToEdit="editedItem" @itemEdited="editedItemSaved" />

        <EditCompanyDialog v-model="editCompanyDialog" v-if="editCompanyDialog" :formTitle="formTitle"
            :itemToEdit="editedItem" @itemEdited="editedItemSaved" />

        <CreateTicketDialog v-model="sendMessageDialog" v-if="sendMessageDialog" :messageItem="messageItem"
            :customerId="customerId" />

        <AlertDialog v-model="alertDialog" v-if="alertDialog" :item="itemToDelete" @itemDeleted="itemDeleted" />

        <OperatorActionsDialog v-model="operatorActionsDialog" v-if="operatorActionsDialog" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <CallBlockDialog v-model="callBlockDialog" v-if="callBlockDialog" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <CancelDeleteDialog v-model="cancelDeleteDialog" v-if="cancelDeleteDialog" :item="itemToCancelDelete"
            @deleteCanceled="deleteCanceled" />

        <FailedBillsDialog v-model="failedBillsDialog" v-if="failedBillsDialog" :bills="failedBills"
            :customerName="customerName" />
    </div>
</template>

<script>

import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'
import Utils from "@/util/utils";
import Auth from '@/services/auth.service';
import EditDialog from '@/components/manuim/dialogs/EditDialog'
import EditPlanDialog from '@/components/manuim/dialogs/EditPlanDialog'
import EditSimDialog from '@/components/manuim/dialogs/EditSimDialog'
import EditPhoneDialog from '@/components/manuim/dialogs/EditPhoneDialog'
import EditCompanyDialog from '@/components/manuim/dialogs/EditCompanyDialog'
import EditCustomerDialog from '@/components/manuim/dialogs/EditCustomer.vue'
import CreateTicketDialog from '@/components/manuim/dialogs/CreateTicketDialog'
import ProgressShow from '@/components/widgets/progressShow.vue'
import AlertDialog from '@/components/manuim/dialogs/AlertDialog'
import OperatorActionsDialog from '@/components/manuim/dialogs/OperatorActionsDialog'
import CallBlockDialog from '@/components/manuim/dialogs/CallBlockDialog'
import CancelDeleteDialog from '@/components/manuim/dialogs/CancelDeleteDialog'
import FailedBillsDialog from '@/components/manuim/dialogs/FailedBillsDialog'

export default {

    components: {
        SnackBar,
        ProgressShow,
        EditDialog,
        EditCustomerDialog,
        CreateTicketDialog,
        AlertDialog,
        EditPlanDialog,
        EditSimDialog,
        EditPhoneDialog,
        EditCompanyDialog,
        OperatorActionsDialog,
        CallBlockDialog,
        CancelDeleteDialog,
        FailedBillsDialog
    },
    data: () => ({
        headers: [
            { text: '#', value: 'index' },
            { text: 'מנוי', value: 'manui_number' },
            { text: 'מפעיל', value: 'plan_id.operating_company' },
            { text: 'מספר סים', value: 'sim_number' },
            { text: 'סוג', value: 'type' },
            { text: 'חבילה', value: 'plan_id.name' },
            { text: 'סטטוס', value: 'status' },
            { text: 'רמת סינון', value: 'community_level' },
            { text: 'משווק', value: 'seller_id.name' },
            { text: 'נוצר בתאריך', value: 'createdAt' },
            { text: 'עודכן בתאריך', value: 'updatedAt' },
            { text: 'תאריך חיוב', value: 'billing_day', sortable: false },
            { text: 'פעולות', value: 'actions', sortable: false },
        ],
        overlay: false,
        manuim: [],
        addEditDialog: false,
        editPlanDialog: false,
        EditCustomerDialog: false,
        editPhoneDialog: false,
        editSimDialog: false,
        editCompanyDialog: false,
        sendMessageDialog: false,
        operatorActionsDialog: false,   // ← להוסיף ליד שאר הדיאלוגים
        callBlockDialog: false,
        formTitle: "",
        editedItem: {},
        messageItem: {},
        itemToDelete: {},
        alertDialog: false,
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        search: "",
        progressShow: false,
        currentPage: 1,
        pageCount: 0,
        itemsPerPage: 20,
        customerId: "",
        customerName: "",
        customerCardId: "",
        role: Auth.getUserRole(),
        companiesList: {
            "clickCallMobile": "קליקול מובייל",
            "hotMobile": "הוט מובייל",
            "partner": "פרטנר"
        },
        cancelDeleteDialog: false,
        itemToCancelDelete: {},
        failedBills: [],
        failedBillsDialog: false,
        checkingFailedBills: false,
        billingDays: {},
    }),
    computed: {
        hasFailedBills() {
            return this.failedBills.length > 0;
        },
        failedBillsCount() {
            return this.failedBills.length;
        },
        failedBillsTotal() {
            return this.failedBills.reduce((sum, bill) => sum + (Number(bill.SumToBill) || 0), 0);
        },
    },
    methods: {
        async getCustomerManuim() {
            try {
                this.progressShow = true;
                let token = localStorage.getItem("token");
                let MyJSON = JSON.stringify({ customer_id: this.customerId });
                let api = process.env.VUE_APP_BASE_URL + `/manuim/get_customer_manuim`;
                const res = await fetch(api, ApiServices.requestOptions("POST", MyJSON, token));
                const jsonObject = await res.json();
                this.progressShow = false;
                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בקבלת נתונים", "red");
                } else if (res.status === 200) {
                    this.manuim = jsonObject;
                    this.formatDetails();
                }
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("Error get files: " + error, "red");
            }
        },
        // בדיקה שקטה ברקע: כל כשל נבלע, לא מוצג דבר אלא אם חזר 200 עם חיובים שנכשלו
        async checkCustomerFailedBills() {
            this.checkingFailedBills = true;
            try {
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/customers/check_failed_bills?customerId=${this.customerId}`;
                const res = await fetch(api, ApiServices.requestOptions("GET", null, token));
                if (res.status !== 200) {
                    return;
                }
                const jsonObject = await res.json();
                if (jsonObject && jsonObject.billingDays && typeof jsonObject.billingDays === 'object') {
                    this.billingDays = jsonObject.billingDays;
                }
                if (jsonObject && jsonObject.hasFailed === true && Array.isArray(jsonObject.failedBills)) {
                    this.failedBills = jsonObject.failedBills;
                }
            } catch (error) {
                // בדיקה שקטה - לא מציגים למשתמש שום דבר במקרה של כשל
            } finally {
                this.checkingFailedBills = false;
            }
        },
        // יום החיוב של המנוי לפי הוראת הקבע שלו בקארדקום
        billingInfo(item) {
            return (item && item.hok_id && this.billingDays[item.hok_id]) || null;
        },
        formatCurrency(value) {
            const num = Number(value) || 0;
            return num.toLocaleString('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 2 });
        },
        formatDetails() {
            this.manuim.forEach(manui => {
                manui.createdAt = Utils.formatDateString(manui.createdAt),
                    manui.updatedAt = Utils.formatDateString(manui.updatedAt)
            });
        },
        formatDateString(date) {
            return Utils.formatDateString(date)
        },
        isPendingCancellation(item) {
            return item && item.status === 'active' && !!item.deletedAt;
        },
        deletionDate(item) {
            if (!item || !item.deletedAt) return null;
            const d = new Date(item.deletedAt);
            return isNaN(d.getTime()) ? null : d;
        },
        // הפרש בימי לוח: 0 = היום, 1 = מחר, שלילי = התאריך עבר
        daysUntilDeletion(item) {
            const target = this.deletionDate(item);
            if (!target) return null;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const t = new Date(target);
            t.setHours(0, 0, 0, 0);
            return Math.round((t.getTime() - today.getTime()) / 86400000);
        },
        isDeletionOverdue(item) {
            const days = this.daysUntilDeletion(item);
            return days !== null && days < 0;
        },
        pendingLabel(item) {
            const days = this.daysUntilDeletion(item);
            if (days === null) return 'בתהליך מחיקה';
            if (days < 0) return `באיחור ${Math.abs(days)} ימים`;
            if (days === 0) return 'נמחק היום';
            if (days === 1) return 'מחר';
            return `בעוד ${days} ימים`;
        },
        formatDateOnly(date) {
            if (!date) return '—';
            const d = new Date(date);
            return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('he-IL');
        },
        getIconForStatus(status) {
            switch (status) {
                case 'active': return 'mdi-check-circle';
                case 'not_active': return 'mdi-cancel';
                case 'proccess': return 'mdi-clock-outline';
                default: return 'rgb(131, 124, 124)';
            }
        },
        getIconColorForStatus(status) {
            switch (status) {
                case 'active': return 'rgb(13, 138, 13)';
                case 'not_active': return 'rgb(206, 18, 18)';
                case 'proccess': return 'rgb(207, 142, 20)';
                default: return '';
            }
        },
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true
        },
        openEditDialog(item, index) {
            this.editedItem = item;
            this.formTitle = "ערוך פרטי מנוי"
            this.addEditDialog = true
        },
        openEditCustomerDialog(item, index) {
            this.editedItem = item;
            this.EditCustomerDialog = true
        },
        openEditPlanDialog(item) {
            this.editedItem = item;
            this.formTitle = "עדכון חבילה למנוי"
            this.editPlanDialog = true
        },
        openEditPhoneDialog(item) {
            this.editedItem = item;
            this.formTitle = "עדכון מספר מנוי"
            this.editPhoneDialog = true
        },
        openEditcompanyDialog(item) {
            this.editedItem = item;
            this.formTitle = "החלפת מפעיל"
            this.editCompanyDialog = true
        },
        openEditSimDialog(item) {
            this.editedItem = item;
            this.formTitle = "עדכון מספר סים"
            this.editSimDialog = true
        },
        openDeleteDialog(item, index) {
            this.itemToDelete = item
            this.formTitle = "האם אתה בטוח שברצונך למחוק את הקהילה?"
            this.alertDialog = true
        },
        editedItemSaved(item, text, color) {
            this.showSnackBar(text, color)
            if (item === "") {
                return
            }
            const index = this.manuim.findIndex(manui => manui._id === item._id);
            Object.assign(this.manuim[index], item);
        },
        itemDeleted(item, text, color) {
            if (!item) {
                this.showSnackBar(text, color)
                return
            }
            const index = this.manuim.findIndex(manui => manui._id === item._id);
            this.manuim.splice(index, 1);
            this.showSnackBar(text, color)
        },
        openCancelDeleteDialog(item) {
            this.itemToCancelDelete = item;
            this.cancelDeleteDialog = true;
        },
        deleteCanceled(item, updated, text, color) {
            const index = this.manuim.findIndex(manui => manui._id === item._id);
            if (index !== -1) {
                this.$set(this.manuim, index, Object.assign({}, this.manuim[index], updated));
            }
            this.showSnackBar(text, color);
        },
        openOperatorActionsDialog(item) {
            this.editedItem = item;
            this.operatorActionsDialog = true;
        },
        hasOperatorActions(item) {
            const operator = item?.plan_id?.operating_company;
            const supportedOperators = ['partner', 'hotMobile', 'clickCallMobile'];
            return supportedOperators.includes(operator) && item.status === 'active';
        },
        openCallBlockDialog(item) {
            this.editedItem = item;
            this.callBlockDialog = true;
        },
        createTicket(item) {
            this.messageItem = item;
            this.sendMessageDialog = true
        },
        hasPermission(action) {
            return this.role === 'admin' || Auth.getUserPermissions().includes(action);
        }
    },
    created() {
        this.customerName = this.$route.params.name;
        this.customerId = this.$route.params.id;
        this.customerCardId = this.$route.params.cardId;
        this.getCustomerManuim();
        this.checkCustomerFailedBills();
    },
}
</script>
<style scoped>
.ms-page {
    --m-navy: #0d2c6d;
    --m-navy-soft: #142f78;
    --m-indigo: #3F51B5;
    --m-indigo-soft: rgba(63, 81, 181, 0.10);
    --m-purple: #8066f0;
    --m-purple-soft: rgba(128, 102, 240, 0.10);
    --m-gold: #fdb721;
    --m-blue: #1976d2;
    --m-blue-soft: rgba(25, 118, 210, 0.10);
    --m-surface: #ffffff;
    --m-bg: #f3f6f9;
    --m-border: #e3e8ef;
    --m-border-soft: #eef2f7;
    --m-text: #0f172a;
    --m-text-soft: #475569;
    --m-text-mute: #94a3b8;
    --m-green: #16804b;
    --m-green-soft: rgba(22, 128, 75, 0.10);
    --m-orange: #c0691c;
    --m-orange-soft: #fff3df;
    --m-red: #c74747;
    --m-red-soft: rgba(199, 71, 71, 0.10);
    --m-shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
    --m-shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
    --m-shadow-lg: 0 18px 40px -12px rgba(13, 44, 109, 0.18), 0 8px 16px -8px rgba(13, 44, 109, 0.10);
    --m-ease: cubic-bezier(0.22, 1, 0.36, 1);
    min-height: 100vh;
    padding-bottom: 60px;
    background: var(--m-bg);
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ─────────────────────── HEADER ─────────────────────── */
.ms-header {
    background: linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
    padding: 12px 0 14px;
    box-shadow: var(--m-shadow-md);
}

.ms-header__inner {
    max-width: 1500px;
    margin: 0 auto;
    padding: 0 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.ms-header__title-wrap,
.ms-customer-card,
.ms-total-pill,
.ms-customer-card__body {
    display: flex;
    align-items: center;
}

.ms-header__title-wrap {
    gap: 12px;
    min-width: 0;
}

.ms-title {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.ms-total-pill {
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.20);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    font-feature-settings: "tnum" 1;
}

.ms-total-pill ::v-deep .v-icon {
    color: rgba(255, 255, 255, 0.85) !important;
}

.ms-customer-card {
    gap: 8px;
    min-width: 0;
    padding: 4px 7px 4px 12px;
    border-radius: 999px;
    background: rgba(253, 183, 33, 0.16);
    border: 1px solid rgba(253, 183, 33, 0.30);
    color: #fff;
    flex-shrink: 0;
}

.ms-customer-card__icon {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    color: #2d1f00;
    background: var(--m-gold);
}

.ms-customer-card__body {
    min-width: 0;
    align-items: flex-start;
    flex-direction: column;
    gap: 0;
}

.ms-customer-card__label {
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1;
}

.ms-customer-card__name {
    max-width: 240px;
    overflow: hidden;
    color: #fff;
    font-size: 0.84rem;
    font-weight: 700;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ms-customer-card__id {
    padding: 3px 8px;
    border-radius: 999px;
    color: var(--m-gold);
    background: rgba(255, 255, 255, 0.10);
    font-size: 0.73rem;
    font-weight: 800;
    direction: ltr;
}

/* ─────────────────────── DEBT CHIP ─────────────────────── */
.ms-header__side {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.ms-debt-probe {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding: 6px 13px 6px 11px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    background: rgba(255, 255, 255, 0.07);
}

.ms-debt-probe__ring {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.22);
    border-top-color: rgba(255, 255, 255, 0.75);
    animation: ms-probe-spin 0.85s linear infinite;
}

.ms-debt-probe__text {
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.73rem;
    font-weight: 600;
    white-space: nowrap;
    animation: ms-probe-breathe 1.9s var(--m-ease) infinite;
}

@keyframes ms-probe-spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes ms-probe-breathe {

    0%,
    100% {
        opacity: 0.55;
    }

    50% {
        opacity: 1;
    }
}

.ms-debt-chip {
    display: flex;
    align-items: center;
    gap: 9px;
    flex-shrink: 0;
    padding: 4px 10px 4px 6px;
    border-radius: 999px;
    border: 1px solid rgba(255, 138, 138, 0.55);
    background: rgba(214, 60, 60, 0.28);
    color: #fff;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.18s var(--m-ease), transform 0.18s var(--m-ease), border-color 0.18s var(--m-ease);
}

.ms-debt-chip:hover {
    background: rgba(214, 60, 60, 0.45);
    border-color: rgba(255, 168, 168, 0.85);
    transform: translateY(-1px);
}

.ms-debt-chip:focus-visible {
    outline: 2px solid #ffd0d0;
    outline-offset: 2px;
}

.ms-debt-chip__icon {
    position: relative;
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    color: #fff;
    background: #d63c3c;
}

.ms-debt-chip__icon::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid rgba(255, 120, 120, 0.9);
    animation: ms-debt-pulse 2.4s var(--m-ease) infinite;
}

.ms-debt-chip__icon ::v-deep .v-icon {
    color: #fff !important;
}

@keyframes ms-debt-pulse {
    0% {
        opacity: 0.75;
        transform: scale(1);
    }

    70%,
    100% {
        opacity: 0;
        transform: scale(1.75);
    }
}

.ms-debt-chip__body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    line-height: 1;
}

.ms-debt-chip__label {
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.65rem;
    font-weight: 700;
}

.ms-debt-chip__value {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #fff;
    font-size: 0.84rem;
    font-weight: 800;
    font-feature-settings: "tnum" 1;
}

.ms-debt-chip__count {
    padding: 2px 6px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    font-size: 0.63rem;
    font-weight: 700;
}

.ms-debt-chip__chev {
    color: rgba(255, 255, 255, 0.75) !important;
}

.ms-debt-fade-enter-active,
.ms-debt-fade-leave-active {
    transition: opacity 0.28s var(--m-ease), transform 0.28s var(--m-ease);
}

.ms-debt-fade-enter,
.ms-debt-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

@media (max-width: 700px) {

    .ms-debt-chip__label,
    .ms-debt-probe__text {
        display: none;
    }
}

@media (prefers-reduced-motion: reduce) {

    .ms-debt-chip,
    .ms-debt-chip__icon::after,
    .ms-debt-probe__ring,
    .ms-debt-probe__text,
    .ms-debt-fade-enter-active,
    .ms-debt-fade-leave-active {
        animation: none !important;
        transition: none !important;
    }
}

/* ─────────────────────── MAIN + SUMMARY ─────────────────────── */
.ms-main {
    max-width: 1700px;
    margin: 22px auto 0;
    padding: 0 22px;
}

.ms-summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(150px, 1fr));
    gap: 12px;
    margin-bottom: 18px;
}

.ms-summary-card {
    display: flex;
    align-items: center;
    gap: 11px;
    min-height: 72px;
    padding: 12px 14px;
    border-radius: 14px;
    background: var(--m-surface);
    border: 1px solid var(--m-border-soft);
    box-shadow: var(--m-shadow-md);
}

.ms-summary-card__icon {
    width: 39px;
    height: 39px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border-radius: 11px;
}

.ms-summary-card__body {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.ms-summary-card__label {
    color: var(--m-text-mute);
    font-size: 0.76rem;
    font-weight: 700;
}

.ms-summary-card__value {
    color: var(--m-text);
    font-size: 1.28rem;
    line-height: 1.05;
    font-weight: 800;
    letter-spacing: -0.01em;
    font-feature-settings: "tnum" 1;
}

.ms-summary-card--all .ms-summary-card__icon {
    color: var(--m-indigo);
    background: var(--m-indigo-soft);
}

.ms-summary-card--active .ms-summary-card__icon {
    color: var(--m-green);
    background: var(--m-green-soft);
}

.ms-summary-card--process .ms-summary-card__icon {
    color: var(--m-orange);
    background: var(--m-orange-soft);
}

.ms-summary-card--inactive .ms-summary-card__icon {
    color: var(--m-red);
    background: var(--m-red-soft);
}

/* ─────────────────────── TOOLBAR ─────────────────────── */
.ms-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 420px) 1fr auto;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
}

.ms-search {
    position: relative;
    display: flex;
    align-items: center;
    height: 42px;
    padding: 0 12px;
    border-radius: 12px;
    background: var(--m-surface);
    border: 1px solid var(--m-border);
    transition: border-color 0.2s var(--m-ease), box-shadow 0.2s var(--m-ease);
}

.ms-search:focus-within {
    border-color: var(--m-navy);
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.10);
}

.ms-search__icon {
    color: var(--m-text-mute) !important;
}

.ms-search__input {
    flex: 1;
    min-width: 0;
    padding: 0 9px;
    border: none;
    outline: none;
    background: transparent;
    color: var(--m-text);
    font-family: inherit;
    font-size: 0.91rem;
}

.ms-search__input::placeholder {
    color: var(--m-text-mute);
}

.ms-search__clear {
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
    border: none;
    border-radius: 50%;
    color: var(--m-text-soft);
    background: var(--m-border-soft);
    cursor: pointer;
}

.ms-toolbar__hint,
.ms-pagesize {
    display: flex;
    align-items: center;
}

.ms-toolbar__hint {
    gap: 6px;
    color: var(--m-text-mute);
    font-size: 0.79rem;
    font-weight: 600;
}

.ms-toolbar__hint ::v-deep .v-icon {
    color: var(--m-indigo) !important;
}

.ms-pagesize {
    gap: 8px;
    color: var(--m-text-soft);
    font-size: 0.84rem;
    font-weight: 700;
}

.ms-pagesize__select {
    padding: 7px 12px;
    border: 1px solid var(--m-border);
    border-radius: 10px;
    outline: none;
    color: var(--m-text);
    background: var(--m-surface);
    font-family: inherit;
    font-size: 0.87rem;
    font-weight: 700;
    cursor: pointer;
}

.ms-pagesize__select:focus {
    border-color: var(--m-navy);
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.10);
}

/* ─────────────────────── TABLE ─────────────────────── */
.ms-table-card {
    overflow: hidden;
    border: 1px solid var(--m-border-soft);
    border-radius: 18px;
    background: var(--m-surface);
    box-shadow: var(--m-shadow-md);
}

::v-deep #table1.v-data-table {
    background: transparent !important;
}

::v-deep #table1.v-data-table .v-data-table-header th {
    height: 52px !important;
    border-bottom: 1px solid var(--m-border-soft) !important;
    border-left: none !important;
    background: #fbfcfe !important;
    text-align: center !important;
}

::v-deep #table1.v-data-table .v-data-table-header th .v-data-table-header__icon {
    color: var(--m-text-mute) !important;
    opacity: 1 !important;
}

.ms-th {
    color: var(--m-text-soft) !important;
    font-family: inherit !important;
    font-size: 0.78rem !important;
    font-weight: 800 !important;
    letter-spacing: 0.02em;
}

::v-deep #table1.v-data-table tbody tr {
    transition: background 0.18s var(--m-ease);
}

::v-deep #table1.v-data-table tbody tr:hover {
    background: #f8fafc !important;
}

::v-deep #table1.v-data-table tbody tr td {
    height: 60px;
    padding: 11px 12px !important;
    border-bottom: 1px solid var(--m-border-soft) !important;
    color: var(--m-text);
    font-family: inherit;
    font-size: 0.88rem;
    text-align: center;
    white-space: nowrap;
}

.ms-index {
    color: var(--m-text-mute);
    font-size: 0.82rem;
    font-weight: 700;
}

.ms-number {
    display: inline-block;
    color: var(--m-text);
    font-feature-settings: "tnum" 1;
    font-weight: 700;
    direction: ltr;
}

.ms-inline-cell,
.ms-actions {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.ms-inline-cell {
    gap: 6px;
}

.ms-company-badge {
    padding: 4px 9px;
    border-radius: 999px;
    color: var(--m-indigo);
    background: var(--m-indigo-soft);
    font-size: 0.78rem;
    font-weight: 800;
}

.ms-plan-name {
    color: var(--m-text);
    font-weight: 700;
}

.ms-billing-day {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--m-text);
    font-weight: 700;
    font-feature-settings: "tnum" 1;
    white-space: nowrap;
}

.ms-billing-day__icon {
    color: var(--m-text-mute) !important;
}

.ms-billing-day--muted {
    color: var(--m-text-mute);
    font-weight: 600;
}

.ms-mini-btn {
    width: 25px;
    height: 25px;
    display: grid;
    place-items: center;
    border: none;
    border-radius: 7px;
    color: var(--m-text-mute);
    background: transparent;
    cursor: pointer;
    transition: all 0.18s var(--m-ease);
}

.ms-mini-btn:hover {
    color: var(--m-indigo);
    background: var(--m-indigo-soft);
}

/* ─────────────────────── STATUS + ACTIONS ─────────────────────── */
.ms-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 9px;
    border-radius: 999px;
    font-size: 0.76rem;
    font-weight: 800;
}

.ms-status-pill__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

.ms-status-pill--active {
    color: var(--m-green);
    background: var(--m-green-soft);
}

.ms-status-pill--process {
    color: var(--m-orange);
    background: var(--m-orange-soft);
}

.ms-status-pill--inactive {
    color: var(--m-red);
    background: var(--m-red-soft);
}

.ms-actions {
    gap: 6px;
}

.ms-action-btn {
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    border: 1px solid var(--m-border);
    border-radius: 9px;
    color: var(--m-text-soft);
    background: var(--m-surface);
    cursor: pointer;
    transition: all 0.18s var(--m-ease);
}

.ms-action-btn:hover {
    border-color: transparent;
    color: #fff;
    transform: translateY(-1px);
}

.ms-action-btn--edit:hover {
    background: var(--m-navy);
    box-shadow: 0 5px 12px -4px rgba(13, 44, 109, 0.60);
}

.ms-action-btn--customer:hover {
    background: var(--m-indigo);
    box-shadow: 0 5px 12px -4px rgba(63, 81, 181, 0.60);
}

.ms-action-btn--ticket:hover {
    background: var(--m-purple);
    box-shadow: 0 5px 12px -4px rgba(128, 102, 240, 0.60);
}

.ms-action-btn--delete:hover {
    background: var(--m-red);
    box-shadow: 0 5px 12px -4px rgba(199, 71, 71, 0.60);
}

.ms-action-btn--operator:hover {
    background: var(--m-gold);
    color: #ffffff;
    box-shadow: 0 5px 12px -4px rgba(253, 183, 33, 0.55);
}

.ms-action-btn--callblock:hover {
    background: var(--m-orange);
    color: #fff;
    border-color: var(--m-orange);
    box-shadow: 0 5px 12px -4px rgba(192, 105, 28, 0.55);
}

/* ─────────────────────── EMPTY + PAGINATION ─────────────────────── */
.ms-empty {
    padding: 50px 20px;
    text-align: center;
}

.ms-empty__icon {
    width: 62px;
    height: 62px;
    display: grid;
    place-items: center;
    margin: 0 auto 14px;
    border-radius: 50%;
    color: var(--m-indigo);
    background: var(--m-indigo-soft);
}

.ms-empty__title {
    margin: 0 0 4px;
    color: var(--m-text);
    font-size: 1.02rem;
}

.ms-empty__hint {
    margin: 0;
    color: var(--m-text-mute);
    font-size: 0.86rem;
}

.ms-pagination-wrap {
    display: flex;
    justify-content: center;
    padding: 17px 12px 12px;
    border-top: 1px solid var(--m-border-soft);
}

::v-deep .ms-pagination-wrap .v-pagination__item,
::v-deep .ms-pagination-wrap .v-pagination__navigation {
    border: 1px solid var(--m-border) !important;
    box-shadow: none !important;
}

::v-deep .ms-pagination-wrap .v-pagination__item--active {
    border-color: var(--m-navy) !important;
    background: var(--m-navy) !important;
    color: #fff !important;
    box-shadow: 0 4px 10px -2px rgba(13, 44, 109, 0.35) !important;
}

/* ─────────────────────── TOOLTIP ─────────────────────── */
.custom-tooltip {
    padding: 10px 12px !important;
    border-radius: 10px;
    color: #fff !important;
    background-color: #2e2e2e !important;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
    direction: rtl;
    font-size: 13px;
    line-height: 1.5;
}

.tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.tooltip-title {
    margin-bottom: 4px;
    color: #fdb721;
    font-size: 14px;
    font-weight: bold;
}

.tooltip-line {
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.cursor-pointer {
    cursor: pointer;
}

/* ─────────────────────── RESPONSIVE ─────────────────────── */
@media (max-width: 1050px) {
    .ms-toolbar {
        grid-template-columns: 1fr auto;
    }

    .ms-toolbar__hint {
        display: none;
    }
}

@media (max-width: 760px) {
    .ms-header__inner {
        align-items: stretch;
        flex-direction: column;
        padding: 0 18px;
    }

    .ms-customer-card {
        min-width: 0;
    }

    .ms-summary-grid {
        grid-template-columns: repeat(2, minmax(130px, 1fr));
    }

    .ms-main {
        margin-top: 16px;
        padding: 0 12px;
    }

    .ms-toolbar {
        grid-template-columns: 1fr;
    }

    .ms-pagesize {
        justify-content: flex-end;
    }
}



.ms-status-pill--process .ms-status-pill__dot {
    animation: ms-pulse 1.8s ease-in-out infinite;
}

@keyframes ms-pulse {

    0%,
    100% {
        box-shadow: 0 0 0 3px rgba(192, 105, 28, 0.18);
    }

    50% {
        box-shadow: 0 0 0 5px rgba(192, 105, 28, 0.04);
    }
}

.ms-summary-card {
    animation: ms-cardIn 0.45s var(--m-ease) backwards;
}

.ms-summary-card:nth-child(1) {
    animation-delay: 0.00s;
}

.ms-summary-card:nth-child(2) {
    animation-delay: 0.06s;
}

.ms-summary-card:nth-child(3) {
    animation-delay: 0.12s;
}

.ms-summary-card:nth-child(4) {
    animation-delay: 0.18s;
}

@keyframes ms-cardIn {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {

    .ms-summary-card,
    .ms-status-pill--process .ms-status-pill__dot {
        animation: none;
    }
}


/* ── פעיל עם מחיקה מתוכננת ── */
.ms-status-pill--pending {
    padding-inline-end: 4px;
    box-shadow: inset 0 0 0 1px rgba(192, 105, 28, 0.3);
}

.ms-pending-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-inline-start: 5px;
    padding: 2px 7px;
    border-radius: 999px;
    background: var(--m-orange-soft);
    color: var(--m-orange);
    font-size: 0.68rem;
    font-weight: 800;
    line-height: 1.4;
    white-space: nowrap;
    animation: ms-pending-glow 2.4s ease-in-out infinite;
}

.ms-pending-badge__icon {
    font-size: 13px !important;
    color: var(--m-orange) !important;
}

.ms-pending-badge--overdue {
    background: var(--m-red-soft);
    color: var(--m-red);
    animation: ms-overdue-glow 1.6s ease-in-out infinite;
}

.ms-pending-badge--overdue .ms-pending-badge__icon {
    color: var(--m-red) !important;
}

@keyframes ms-pending-glow {

    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(192, 105, 28, 0.3);
    }

    50% {
        box-shadow: 0 0 0 4px rgba(192, 105, 28, 0);
    }
}

@keyframes ms-overdue-glow {

    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.35);
    }

    50% {
        box-shadow: 0 0 0 5px rgba(211, 47, 47, 0);
    }
}

@media (prefers-reduced-motion: reduce) {

    .ms-pending-badge,
    .ms-pending-badge--overdue {
        animation: none;
    }
}

.tooltip-title--warn {
    display: flex;
    align-items: center;
    color: #ffb01f;
}

.tooltip-title--danger {
    display: flex;
    align-items: center;
    color: #ff6b6b;
}

.tooltip-note {
    margin-top: 5px;
    padding-top: 5px;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
}

.ms-tt-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 9px;
    padding: 7px 10px;
    border: 1px solid rgba(140, 233, 154, 0.35);
    border-radius: 8px;
    background: rgba(140, 233, 154, 0.12);
    color: #8ce99a;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
}

.ms-tt-btn:hover {
    background: #8ce99a;
    border-color: #8ce99a;
    color: #14351b;
}

.ms-tt-btn:hover .v-icon {
    color: #14351b !important;
}
</style>


<style>
.ms-pending-menu {
    padding: 11px 13px;
    border-radius: 10px;
    background-color: #2e2e2e !important;
    color: #fff;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35) !important;
    direction: rtl;
    font-size: 13px;
    line-height: 1.5;
    contain: none !important;
}
</style>